/*
 * AI 问照片「内测申请」付费弹窗 V2。
 *
 * 三态单弹窗:
 *   - select  选档位 + 内测专享价 + 限量名额 + [联系客服咨询][立即申请]
 *   - pay     扫码支付(复用 VipPaymentModal 的 PaymentQRCode)
 *   - success 申请成功(客户经理 1V1 跟进开通)
 *
 * 业务定性:内测期不动计费,这里只做「付费内测申请 + 留资收单」。支付成功后
 * 把订单 + 用户 / 团队信息上报给 xiaohei-claw 存库(POST /web/xiaohei-claw/beta/order),
 * 客户经理从库里捞数据主动联系开通,对应文案「7 天内开通」。
 */
import React, { useState, useMemo, useEffect, useCallback } from 'react'
import ReactDOM from 'react-dom'
import {
  CheckCircleFilled,
  CheckOutlined,
  CloseOutlined,
  LeftOutlined
} from '@ant-design/icons'
import { useAppSelector } from '../../../app/hooks'
import PaymentQRCode from '../../components/VipPaymentModal/components/PaymentQRCode'
import { useAiPhotoBetaOrder } from '../hooks/useAiPhotoBetaOrder'
import {
  AI_PHOTO_BETA_PLANS,
  AI_PHOTO_BETA_BENEFITS,
  AI_PHOTO_BETA_DEFAULT_PLAN_KEY,
  AI_PHOTO_BETA_PRODUCT_KEY
} from '../aiPhotoBetaGoods'
import { trackAiBetaExposure, trackAiBetaClick } from '../utils/aiPhotoJunTrack'
import './AiPhotoBetaModal.scss'

/** 拼接 xiaohei-claw 接口要用的全局后端域名(构建期注入) */
declare const __API_SERVER_HOST__: string

interface Props {
  visible: boolean
  /** 关闭弹窗 */
  onClose: () => void
  /**
   * 来源标识,支付成功上报时带上,后端按 source 区分不同入口的转化。
   * 例:'ai_photo_banner'(问照片 banner) / 'ai_photo_insufficient'(积分不足弹窗)。
   */
  source?: string
  /** 点「联系客服咨询」回调;不传则隐藏该按钮(其他业务无客服入口时) */
  onContactService?: () => void
  /** 当前 AI 会话 ID(c8147 埋点用);非会话场景(如 AI 分类问卷入口)不传, 上报空串 */
  sessionID?: string
  /**
   * 名额(后端 beta/quota 返回, 展示「仅限前 N 名 · 还剩 M 个」)。
   * 外部已有就传进来直接用;不传则弹窗打开时自己拉 beta/quota —— 实现自包含、
   * 可被任意业务直接 import 引入,只需给 visible / onClose / source。
   */
  total?: number
  remaining?: number
}

/** 弹窗当前所处步骤 */
type Step = 'select' | 'pay' | 'success'

/** 把额度张数格式化为「5000 / 1万 / 3万 / 10万」 */
const formatQuota = (n: number): string => {
  if (n >= 10000) {
    const w = n / 10000
    return `${Number.isInteger(w) ? w : w.toFixed(1)}万`
  }
  return String(n)
}

const AiPhotoBetaModal: React.FC<Props> = ({
  visible,
  onClose,
  source = '',
  onContactService,
  sessionID = '',
  total: totalProp,
  remaining: remainingProp
}) => {
  // 用户 / 团队信息:支付成功上报存库时带上, 方便客户经理联系
  const user = useAppSelector((s: any) => s.main.user)
  const group = useAppSelector((s: any) => s.group)

  const [step, setStep] = useState<Step>('select')
  const [selectedPlanKey, setSelectedPlanKey] = useState(
    AI_PHOTO_BETA_DEFAULT_PLAN_KEY
  )
  const [paidOrderNo, setPaidOrderNo] = useState('')

  // 名额:外部传了就用外部的,否则弹窗自己拉 beta/quota(自包含,可被任意业务直接引入)
  const [quota, setQuota] = useState<{ total: number; remaining: number }>({
    total: typeof totalProp === 'number' ? totalProp : 100,
    remaining: typeof remainingProp === 'number' ? remainingProp : 0
  })
  // 外部名额变化时同步进来
  useEffect(() => {
    if (typeof totalProp === 'number' || typeof remainingProp === 'number') {
      setQuota({
        total: typeof totalProp === 'number' ? totalProp : 100,
        remaining: typeof remainingProp === 'number' ? remainingProp : 0
      })
    }
  }, [totalProp, remainingProp])
  // 外部没传名额时,弹窗打开自拉 beta/quota
  useEffect(() => {
    if (!visible) return
    if (typeof totalProp === 'number' && typeof remainingProp === 'number') {
      return
    }
    let disposed = false
    fetch(
      `${__API_SERVER_HOST__}/web/xiaohei-claw/beta/quota?product_key=${encodeURIComponent(
        AI_PHOTO_BETA_PRODUCT_KEY
      )}`,
      { method: 'GET', credentials: 'include' }
    )
      .then((r) => (r.ok ? r.json() : null))
      .then((body) => {
        if (disposed || !body) return
        // xiaohei-claw 直接返回 { total, remaining };兼容个别网关包一层 data
        const data =
          body && typeof body.data === 'object' && body.data ? body.data : body
        setQuota({
          total: typeof data?.total === 'number' ? data.total : 100,
          remaining: typeof data?.remaining === 'number' ? data.remaining : 0
        })
      })
      .catch((e) => console.warn('[AiPhotoBeta] 拉名额失败,忽略', e))
    return () => {
      disposed = true
    }
  }, [visible, totalProp, remainingProp])

  const currentPlan = useMemo(
    () =>
      AI_PHOTO_BETA_PLANS.find((p) => p.key === selectedPlanKey) ||
      AI_PHOTO_BETA_PLANS[0],
    [selectedPlanKey]
  )

  /** 立省金额(原价 − 现价)与折扣(几折) */
  const saved = currentPlan.originPrice - currentPlan.price
  const discount = Math.round(
    (currentPlan.price / currentPlan.originPrice) * 10
  )

  /**
   * 支付成功:上报订单 + 用户 / 团队信息到 xiaohei-claw 存库, 再切成功态。
   * 上报失败不阻断成功态 —— 用户已付款, 客服可从订单系统兜底查到。
   */
  const handlePaidReport = useCallback(
    async (orderNo: string) => {
      const payload = {
        order_no: orderNo,
        product_key: AI_PHOTO_BETA_PRODUCT_KEY,
        // 来源标识:后端按 source 区分不同入口转化
        source,
        goods_id: currentPlan.goodsID,
        quota_size: currentPlan.quota,
        // 档位标价转分(展示口径, 非实付);实付以订单系统为准
        amount: Math.round(currentPlan.price * 100),
        user_id: user?.user_id || '',
        nickname: user?.nickname || '',
        mobile: user?.mobile || '',
        group_id: group?.groupId || '',
        group_name: group?.groupName || ''
      }
      try {
        await fetch(`${__API_SERVER_HOST__}/web/xiaohei-claw/beta/order`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify(payload)
        })
      } catch (e) {
        console.error('[AiPhotoBeta] 上报存库失败, 忽略(客服可兜底)', e)
      }
      // 支付成功的转化上报由服务端埋点 ServerOrderDataSyncSuc4 + beta_order 落库覆盖,
      // 平台未登记前端 paid 事件(2026-06-11 spec), 前端不再单独上报。
      setPaidOrderNo(orderNo)
      setStep('success')
    },
    [currentPlan, user, group, source]
  )

  // 只有进入 pay 步骤才把商品交给下单 hook, 触发 createPaymentOrder
  const payingGoods = useMemo(
    () => (step === 'pay' ? { id: currentPlan.goodsID } : null),
    [step, currentPlan]
  )

  const {
    alipayUrl,
    wechatUrl,
    qrCodeInvalid,
    channel,
    setChannel,
    isCreatingOrder,
    createOrder,
    clearTimers
  } = useAiPhotoBetaOrder(payingGoods, { onPaymentSuccess: handlePaidReport })

  // 弹窗打开 → 曝光埋点;关闭 → 清轮询定时器并重置状态
  useEffect(() => {
    if (visible) {
      trackAiBetaExposure({
        sessionID,
        remaining: quota.remaining,
        planKey: selectedPlanKey
      })
    } else {
      clearTimers()
      setStep('select')
      setSelectedPlanKey(AI_PHOTO_BETA_DEFAULT_PLAN_KEY)
      setPaidOrderNo('')
    }
    // 仅在 visible 切换时执行, 其余依赖只取当时值
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visible])

  if (!visible) return null

  /** 切换档位 */
  const handlePickPlan = (key: string) => {
    if (key === selectedPlanKey) return
    setSelectedPlanKey(key)
    trackAiBetaClick({
      clickItem: '切换档位',
      sessionID,
      planKey: key,
      remaining: quota.remaining
    })
  }

  /** 点「立即申请」→ 进入支付 */
  const handleApply = () => {
    trackAiBetaClick({
      clickItem: '立即申请',
      sessionID,
      planKey: currentPlan.key,
      remaining: quota.remaining
    })
    setStep('pay')
  }

  /** 联系客服咨询 */
  const handleContact = () => {
    trackAiBetaClick({
      clickItem: '联系客服咨询',
      sessionID,
      planKey: currentPlan.key,
      remaining: quota.remaining
    })
    onContactService && onContactService()
  }

  /** 关闭 */
  const handleClose = () => {
    trackAiBetaClick({
      clickItem: '关闭弹窗',
      sessionID,
      planKey: currentPlan.key,
      remaining: quota.remaining
    })
    onClose()
  }

  return ReactDOM.createPortal(
    <div className="ai-beta-modal-mask" onClick={handleClose}>
      <div className="ai-beta-modal" onClick={(e) => e.stopPropagation()}>
        <button
          type="button"
          className="ai-beta-modal__close"
          onClick={handleClose}
          aria-label="关闭"
        >
          <CloseOutlined />
        </button>

        {/* ============ 选择档位 ============ */}
        {step === 'select' && (
          <div className="ai-beta-modal__select">
            <div className="ai-beta-modal__heading">
              请选择要识别画面的照片数量 <span className="is-required">*</span>
            </div>

            {/* 档位卡 */}
            <div className="ai-beta-modal__plans">
              {AI_PHOTO_BETA_PLANS.map((plan) => (
                <button
                  type="button"
                  key={plan.key}
                  className={
                    'ai-beta-modal__plan' +
                    (plan.key === selectedPlanKey ? ' is-active' : '')
                  }
                  onClick={() => handlePickPlan(plan.key)}
                >
                  {plan.key === selectedPlanKey && (
                    <span className="ai-beta-modal__plan-check">
                      <CheckOutlined />
                    </span>
                  )}
                  <span className="ai-beta-modal__plan-quota">
                    {formatQuota(plan.quota)}
                  </span>
                  <span className="ai-beta-modal__plan-duration">
                    {plan.durationLabel}
                  </span>
                </button>
              ))}
            </div>

            {/* 内测专享价大卡 */}
            <div className="ai-beta-modal__price-card">
              <div className="ai-beta-modal__price-card-top">
                <span className="ai-beta-modal__price-tag">🔥 内测专享价</span>
                <span className="ai-beta-modal__price-save">
                  ⚡ 立省 ¥{saved}({discount}折)
                </span>
              </div>
              <div className="ai-beta-modal__price-main">
                <span className="ai-beta-modal__price-symbol">¥</span>
                <span className="ai-beta-modal__price-number">
                  {currentPlan.price}
                </span>
                <span className="ai-beta-modal__price-origin">
                  ¥{currentPlan.originPrice}
                </span>
              </div>
              <ul className="ai-beta-modal__benefits">
                {AI_PHOTO_BETA_BENEFITS.map((text, i) => (
                  <li key={i}>
                    {i === 0 ? (
                      <span className="ai-beta-modal__benefit-emoji">📷</span>
                    ) : (
                      <CheckCircleFilled />
                    )}
                    <span>{text}</span>
                  </li>
                ))}
              </ul>
            </div>

            {/* 限量名额 */}
            <div className="ai-beta-modal__quota-tip">
              仅限前 {quota.total} 名 · 还剩 <strong>{quota.remaining}</strong>{' '}
              个名额
            </div>

            {/* 操作 */}
            <div className="ai-beta-modal__actions">
              {/* 联系客服:仅当外部传了回调才展示(其他业务无客服入口时自动隐藏) */}
              {onContactService && (
                <button
                  type="button"
                  className="ai-beta-modal__btn is-ghost"
                  onClick={handleContact}
                >
                  联系客服咨询
                </button>
              )}
              <button
                type="button"
                className="ai-beta-modal__btn is-primary"
                onClick={handleApply}
              >
                立即申请(¥{currentPlan.price})
              </button>
            </div>
          </div>
        )}

        {/* ============ 扫码支付 ============ */}
        {step === 'pay' && (
          <div className="ai-beta-modal__pay">
            <button
              type="button"
              className="ai-beta-modal__back"
              onClick={() => setStep('select')}
            >
              <LeftOutlined /> 返回
            </button>
            <div className="ai-beta-modal__pay-head">
              <div className="ai-beta-modal__pay-title">
                {formatQuota(currentPlan.quota)}额度 · 内测专享
              </div>
              <div className="ai-beta-modal__pay-price">
                <span>¥</span>
                {currentPlan.price}
              </div>
            </div>
            <PaymentQRCode
              alipayUrl={alipayUrl}
              wechatUrl={wechatUrl}
              qrCodeInvalid={qrCodeInvalid}
              onRefresh={() => createOrder(true)}
              channel={channel}
              price={currentPlan.price}
              setChannel={(c: number) => setChannel(c)}
              alipayOnly={false}
              isContinuous={false}
              isCreatingOrder={isCreatingOrder}
            />
          </div>
        )}

        {/* ============ 申请成功 ============ */}
        {step === 'success' && (
          <div className="ai-beta-modal__success">
            <div className="ai-beta-modal__success-icon">
              <CheckCircleFilled />
            </div>
            <div className="ai-beta-modal__success-title">申请成功!</div>
            <div className="ai-beta-modal__success-desc">
              客户经理将在 7 天内与你联系，协助开通专属识别额度
            </div>
            <div className="ai-beta-modal__success-order">
              <span>订单编号</span>
              <span className="is-id">{paidOrderNo}</span>
            </div>
            <button
              type="button"
              className="ai-beta-modal__btn is-primary is-block"
              onClick={onClose}
            >
              我知道了
            </button>
          </div>
        )}
      </div>
    </div>,
    document.body
  )
}

export default AiPhotoBetaModal
